import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import SoloStudentExpense from "./SoloStudentExpense";
import PaymentHistoryStatistics from "./SoloPaymentHistoryStatistics";
import SoloPaymentStatistics from "./SoloPaymentStatistics";

// Import PDF and print related icons
import {
  FaSearch,
  FaUser,
  FaIdCard,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaLock,
  FaLockOpen,
  FaCheckCircle,
  FaEye,
  FaHistory,
  FaRupeeSign,
  FaFilter,
  FaTimes,
  FaPaperPlane,
  FaCreditCard,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSync,
  FaTrash,
  FaEdit,
  FaFileInvoiceDollar,
  FaRegCalendarCheck,
  FaRegCalendarTimes,
  FaChartLine,
  FaUsers,
  FaArrowRight,
  FaBan,
  FaTools,
  FaCog,
  FaPercent,
  FaCalendarCheck,
  FaCalendarDay,
  FaDatabase,
  FaPrint,
  FaFilePdf,
  FaReceipt,
  FaDownload,
  FaFileAlt,
  FaFileExport,
} from "react-icons/fa";

import "./PaySoloStdFee.css";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = moment().year();
const years = [currentYear - 1, currentYear, currentYear + 1];

const PaySoloStdFee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState("fee");

  // ---------- STATE DECLARATIONS ----------
  const [studentIdPart2, setStudentIdPart2] = useState("");
  const [studentIdPart3, setStudentIdPart3] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Student main data
  const [studentData, setStudentData] = useState({
    studentId: "",
    studentName: "",
    fatherName: "",
    studentClass: "",
    mobile: "",
    b_form_no: "",
    address: "",
    gender: "",
    studentFee: "",
    section: "",
    family_status: "",
    pendingFee: 0,
    admissionDate: "",
    discount: 0,
  });

  // Fee records & payment form
  const [feeRecords, setFeeRecords] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [feeFormData, setFeeFormData] = useState({
    feePaid: "",
    feePending: "0",
    totalFee: "0",
    discountType: "percentage",
    discountValue: "0",
    paymentMode: "cash",
    transactionId: "",
    remarks: "",
  });

  // Validation & warning flags
  const [isFamilyStudent, setIsFamilyStudent] = useState(false);
  const [familyStudentData, setFamilyStudentData] = useState(null);
  const [isFeeSetupIncomplete, setIsFeeSetupIncomplete] = useState(false);
  const [feeSetupValidationData, setFeeSetupValidationData] = useState(null);
  const [isAutoFixing, setIsAutoFixing] = useState(false);

  // ---------- NEW: INACTIVE STUDENT STATE ----------
  const [isInactive, setIsInactive] = useState(false);
  const [inactiveStudentData, setInactiveStudentData] = useState(null);

  // Payment history & statistics
  const [paymentHistoryData, setPaymentHistoryData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Database status
  const [isDbConnected, setIsDbConnected] = useState(true);
  const [dbStats, setDbStats] = useState(null);
  const [isLoadingDb, setIsLoadingDb] = useState(false);

  // Receipt / report dropdown
  const [showReceiptOptions, setShowReceiptOptions] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // ---------- INITIAL STATE OBJECTS (for reset) ----------
  const initialStudentState = {
    studentId: "",
    studentName: "",
    fatherName: "",
    studentClass: "",
    mobile: "",
    b_form_no: "",
    address: "",
    gender: "",
    studentFee: "",
    section: "",
    family_status: "",
    pendingFee: 0,
    admissionDate: "",
    discount: 0,
  };

  const initialFeeFormState = {
    feePaid: "",
    feePending: "0",
    totalFee: "0",
    discountType: "percentage",
    discountValue: "0",
    paymentMode: "cash",
    transactionId: "",
    remarks: "",
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowReceiptOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const storedId = searchParams.get("studentId");
    if (storedId) {
      const parts = storedId.split("-");
      if (parts.length === 3) {
        setStudentIdPart2(parts[1]);
        setStudentIdPart3(parts[2]);
      }
    }
  }, [location]);

  useEffect(() => {
    if (studentIdPart2.length === 3 && studentIdPart3.length === 7) {
      const timer = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [studentIdPart2, studentIdPart3]);

  const calculateTotalPendingFee = () => {
    if (!studentData.studentId || feeRecords.length === 0) return 0;

    const monthMap = {};
    feeRecords.forEach((record) => {
      const month = record.paid_month;
      if (
        !monthMap[month] ||
        new Date(record.created_at) > new Date(monthMap[month].created_at)
      ) {
        monthMap[month] = record;
      }
    });

    let totalPending = 0;
    Object.values(monthMap).forEach((record) => {
      totalPending += parseFloat(record.fee_pending || 0);
    });

    return totalPending;
  };

  useEffect(() => {
    if (studentData.studentId && feeRecords.length > 0) {
      const totalPending = calculateTotalPendingFee();
      if (studentData.pendingFee !== totalPending) {
        setStudentData((prev) => ({
          ...prev,
          pendingFee: totalPending,
        }));
      }
    }
  }, [feeRecords, studentData.studentId]);

  // ============ RECEIPT AND REPORT FUNCTIONS ============

  const generateReceiptHTML = (type = "current", selectedReceiptData = null) => {
    const currentDate = moment().format("DD-MM-YYYY hh:mm A");
    const receiptNumber = selectedReceiptData?.receipt_no || `REC-${Date.now().toString().slice(-6)}`;

    // Data mapping
    let title = "FEE PAYMENT RECEIPT";
    let monthsList = selectedMonths.join(", ");
    let amountPaid = parseFloat(feeFormData.feePaid) || 0;
    let totalFee = calculateTotalFee();
    let discountAmount = calculateFeeDiscountAmount();
    let payableAmount = calculateDiscountedFee();
    let balanceAmount = payableAmount - amountPaid;

    if (type === "complete") {
      title = "ANNUAL FEE REPORT";
      monthsList = "Full Academic Session";
      const report = calculateCompleteFeeReport();
      amountPaid = report.totalPaid;
      totalFee = report.totalFee;
      discountAmount = report.totalDiscount;
      payableAmount = report.payableAmount;
      balanceAmount = report.pendingAmount;
    } else if (type === "view" && selectedReceiptData) {
      title = "PAYMENT RECEIPT";
      monthsList = selectedReceiptData.paid_month;
      amountPaid = parseFloat(selectedReceiptData.fee_paid);
      totalFee = parseFloat(selectedReceiptData.monthly_fee);
      discountAmount = parseFloat(selectedReceiptData.discount_amount);
      payableAmount = totalFee - discountAmount;
      balanceAmount = parseFloat(selectedReceiptData.fee_pending);
    }

    return `
      <html>
        <head>
          <title>Dar-e-Arqam_Receipt_${studentData.studentId}</title>
          <style>
            @page { size: A4; margin: 0; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; background: #f0f2f5; padding: 30px; }
            
            /* Professional Gradient Bubbles */
            .bubble { position: absolute; border-radius: 50%; filter: blur(50px); z-index: 0; pointer-events: none; }
            .b1 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(26, 42, 108, 0.1) 0%, transparent 70%); top: -150px; right: -150px; }
            .b2 { width: 450px; height: 450px; background: radial-gradient(circle, rgba(178, 148, 94, 0.08) 0%, transparent 70%); bottom: -100px; left: -100px; }
            .b3 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(26, 42, 108, 0.05) 0%, transparent 70%); top: 30%; left: 20%; }

            .receipt-card {
              width: 210mm;
              background: white;
              border: 1px solid #e0e0e0;
              position: relative;
              overflow: hidden; /* Important to clip bubbles */
              padding: 50px;
              box-sizing: border-box;
              box-shadow: 0 10px 30px rgba(0,0,0,0.1);
              margin: 0 auto;
            }

            .header { display: flex; align-items: center; border-bottom: 4px solid #1a2a6c; padding-bottom: 25px; margin-bottom: 30px; position: relative; z-index: 1;}
            .logo-container img { height: 110px; margin-right: 30px; filter: drop-shadow(2px 4px 6px rgba(0,0,0,0.1)); }
            
            .school-details h1 { 
                margin: 0; color: #1a2a6c; font-size: 34px; 
                font-family: 'Times New Roman', Times, serif; font-weight: 900; 
                text-transform: uppercase; letter-spacing: 1px; 
            }
            .school-details p { margin: 4px 0; color: #b2945e; font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; }
            
            .receipt-id-box { margin-left: auto; text-align: right; }
            .receipt-id-box h2 { margin: 0; color: #1a2a6c; font-size: 24px; border-bottom: 3px solid #b2945e; display: inline-block; padding-bottom: 5px; }
            .receipt-id-box p { margin: 10px 0 0 0; font-family: 'Courier New', monospace; font-size: 16px; font-weight: bold; color: #333; }

            .info-grid { 
                display: grid; grid-template-columns: 1fr 1fr; gap: 20px; 
                background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(5px);
                padding: 25px; border: 1px solid #eee; border-left: 6px solid #b2945e;
                margin-bottom: 35px; position: relative; z-index: 1;
            }
            .info-item { font-size: 15px; color: #333; border-bottom: 1px dotted #ddd; padding-bottom: 8px; }
            .info-item strong { color: #1a2a6c; width: 125px; display: inline-block; font-weight: 700; }

            .fee-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; position: relative; z-index: 1; background: white; }
            .fee-table th { background: #1a2a6c; color: white; padding: 15px; text-align: left; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
            .fee-table td { padding: 15px; border-bottom: 1px solid #eee; font-size: 15px; color: #2c3e50; }
            
            .summary-wrapper { display: flex; justify-content: flex-end; position: relative; z-index: 1; }
            .amount-summary { width: 380px; }
            .amount-row { display: flex; justify-content: space-between; padding: 12px 0; font-size: 15px; border-bottom: 1px solid #f9f9f9; }
            .amount-row.total { border-top: 3px solid #1a2a6c; margin-top: 10px; font-weight: 900; font-size: 20px; color: #1a2a6c; border-bottom: none; }
            .amount-row.paid { color: #27ae60; font-weight: bold; background: #f0fff4; padding: 12px; border-radius: 6px; margin: 5px 0; border: 1px solid #c6f6d5; }
            .amount-row.pending { color: #c0392b; font-weight: 700; }

            .footer-legal { 
                display: flex; justify-content: space-between; align-items: flex-end; 
                margin-top: 90px; position: relative; z-index: 1;
            }
            
            /* Signature on Left */
            .sig-container { text-align: center; width: 220px; padding-bottom: 10px; }
            .sig-line { border-top: 2px solid #1a2a6c; margin-bottom: 12px; }
            .sig-container strong { color: #1a2a6c; text-transform: uppercase; font-size: 13px; letter-spacing: 1px; }
            
            /* LARGE STAMP ON RIGHT */
            .stamp-container { width: 220px; text-align: center; position: relative; }
            .official-stamp-img { 
                position: absolute; 
                top: -125px; 
                left: 20px; 
                width: 190px; /* INCREASED SIZE */
                opacity: 0.92; 
                transform: rotate(-12deg); 
                filter: contrast(1.1) brightness(0.95);
                mix-blend-mode: multiply; 
            }
            .stamp-label { font-size: 11px; color: #ccc; text-transform: uppercase; font-weight: 600; letter-spacing: 1px; display: block; margin-top: 60px; }

            .bottom-disclaimer { 
                text-align: center; font-size: 11px; color: #95a5a6; 
                margin-top: 60px; border-top: 1px solid #eee; padding-top: 20px; 
                font-style: italic; line-height: 1.6;
            }
          </style>
        </head>
        <body>
          <div class="receipt-card">
            <div class="bubble b1"></div>
            <div class="bubble b2"></div>
            <div class="bubble b3"></div>

            <div class="header">
              <div class="logo-container">
                <img src="/images/l1.jpeg" alt="Logo" onerror="this.src='/images/l2.jpeg'">
              </div>
              <div class="school-details">
                <h1>DAR-E-ARQAM SCHOOL</h1>
                <p>KOTLI LOHARAN EAST CAMPUS | SIALKOT</p>
              </div>
              <div class="receipt-id-box">
                <h2>${title}</h2>
                <p>REF: ${receiptNumber}</p>
              </div>
            </div>

            <div class="info-grid">
              <div class="info-item"><strong>Student ID:</strong> ${studentData.studentId}</div>
              <div class="info-item"><strong>Class:</strong> ${studentData.studentClass} - ${studentData.section}</div>
              <div class="info-item"><strong>Student Name:</strong> ${studentData.studentName}</div>
              <div class="info-item"><strong>Father Name:</strong> ${studentData.fatherName}</div>
              <div class="info-item"><strong>Payment Date:</strong> ${currentDate}</div>
              <div class="info-item"><strong>Mode:</strong> ${feeFormData.paymentMode.toUpperCase()}</div>
            </div>

            <table class="fee-table">
              <thead>
                <tr>
                  <th>Particulars / Description</th>
                  <th style="text-align: right;">Amount (PKR)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Fee payment for the month(s) of: <strong>${monthsList}</strong></td>
                  <td style="text-align: right;">${totalFee.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                </tr>
                <tr>
                  <td>Concession / Discount Applied</td>
                  <td style="text-align: right; color: #27ae60;">- ${discountAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                </tr>
              </tbody>
            </table>

            <div class="summary-wrapper">
              <div class="amount-summary">
                <div class="amount-row">
                  <span>Net Payable Amount:</span>
                  <span>Rs. ${payableAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div class="amount-row paid">
                  <span>Total Amount Received:</span>
                  <span>Rs. ${amountPaid.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div class="amount-row total pending">
                  <span>Balance Outstanding:</span>
                  <span>Rs. ${balanceAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </div>

            <div class="footer-legal">
              <div class="sig-container">
                <div class="sig-line"></div>
                <strong>Authorized Signature</strong>
              </div>

              <div class="stamp-container">
                <img src="/images/stamp.png" class="official-stamp-img" alt="Stamp">
                <span class="stamp-label">Official Institution Stamp</span>
              </div>
            </div>

            <div class="bottom-disclaimer">
              This is an official computer-generated receipt of Dar-e-Arqam School. No manual signature required if stamped.<br>
              <strong>Software Developed by:</strong> MHS Developers | Meta Hybrid Solutions (MetaHybrid.com)
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const generateMonthlyBreakdownHTML = () => {
    let html = `
      <div class="monthly-breakdown">
        <h3>Monthly Fee Breakdown (${currentYear})</h3>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Monthly Fee</th>
              <th>Paid Amount</th>
              <th>Pending Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
    `;

    months.forEach(month => {
      const record = feeRecords
        .filter(r => r.paid_month === month)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      
      const monthlyFee = parseFloat(studentData.studentFee) || 0;
      const paidAmount = record ? parseFloat(record.fee_paid) : 0;
      const pendingAmount = record ? parseFloat(record.fee_pending) : monthlyFee;
      const status = getMonthStatus(month);
      const statusClass = `status-${status}`;
      
      html += `
        <tr>
          <td>${month}</td>
          <td>Rs. ${monthlyFee.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
          <td>Rs. ${paidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
          <td>Rs. ${pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
          <td><span class="status-badge ${statusClass}">${status.toUpperCase()}</span></td>
        </tr>
      `;
    });

    html += `
          </tbody>
        </table>
      </div>
    `;
    
    return html;
  };

  const handlePrintReceipt = (type = "current") => {
    if (!studentData.studentId) {
      Swal.fire({
        title: "No Student Data",
        text: "Please search for a student first.",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    if (type === "current" && (selectedMonths.length === 0 || !feeFormData.feePaid)) {
      Swal.fire({
        title: "No Payment Data",
        text: "Please complete a payment first or select receipt from history.",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    const receiptWindow = window.open("", "_blank");
    receiptWindow.document.write(generateReceiptHTML(type));
    receiptWindow.document.close();
    
    setTimeout(() => {
      receiptWindow.focus();
      receiptWindow.print();
    }, 500);
  };

  const handleViewReceipt = (receiptData = null) => {
    if (!studentData.studentId) {
      Swal.fire({
        title: "No Student Data",
        text: "Please search for a student first.",
        icon: "warning",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    const type = receiptData ? "view" : "current";
    
    Swal.fire({
      title: "Fee Receipt",
      html: `
        <div style="text-align: center; margin-bottom: 20px;">
          <FaReceipt style="font-size: 48px; color: #3498db; margin-bottom: 15px;" />
          <h3>Receipt Preview</h3>
          <p>Preview of the fee receipt. You can print or download as PDF.</p>
        </div>
        <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
            <strong>Student:</strong> ${studentData.studentName}
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
            <strong>Class:</strong> ${studentData.studentClass} - ${studentData.section}
          </div>
          <div style="display: flex; justify-content: space-between;">
            <strong>Amount:</strong> Rs. ${(parseFloat(feeFormData.feePaid) || 0).toLocaleString("en-IN")}
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Print Receipt",
      cancelButtonText: "Download PDF",
      showDenyButton: true,
      denyButtonText: "View Full Receipt",
      customClass: { popup: "swal-wide" },
      width: 600,
    }).then((result) => {
      if (result.isConfirmed) {
        handlePrintReceipt(type);
      } else if (result.isDenied) {
        const receiptWindow = window.open("", "_blank");
        receiptWindow.document.write(generateReceiptHTML(type, receiptData));
        receiptWindow.document.close();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        handleExportPDF(type);
      }
    });
  };

  const handleExportPDF = async (type = "current") => {
    setIsGeneratingReport(true);
    
    try {
      const receiptWindow = window.open("", "_blank");
      receiptWindow.document.write(generateReceiptHTML(type));
      receiptWindow.document.close();
      
      Swal.fire({
        title: "PDF Export",
        text: "PDF generation started. The receipt has been opened in a new window.",
        icon: "info",
        customClass: { popup: "swal-wide" },
      });
      
    } catch (error) {
      console.error("PDF generation error:", error);
      Swal.fire({
        title: "Export Failed",
        text: "Failed to generate PDF. Please try again.",
        icon: "error",
        customClass: { popup: "swal-wide" },
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const calculateCompleteFeeReport = () => {
    const totalFee = parseFloat(studentData.studentFee || 0) * 12;
    let totalPaid = 0;
    let totalDiscount = 0;
    
    const monthMap = {};
    feeRecords.forEach(record => {
      const month = record.paid_month;
      if (!monthMap[month]) {
        monthMap[month] = {
          paid: 0,
          discount: 0
        };
      }
      monthMap[month].paid = Math.max(monthMap[month].paid, parseFloat(record.fee_paid || 0));
      monthMap[month].discount = Math.max(monthMap[month].discount, parseFloat(record.discount_amount || 0));
    });
    
    Object.values(monthMap).forEach(data => {
      totalPaid += data.paid;
      totalDiscount += data.discount;
    });
    
    const payableAmount = totalFee - totalDiscount;
    const pendingAmount = payableAmount - totalPaid;
    
    return {
      totalFee,
      totalPaid,
      totalDiscount,
      payableAmount,
      pendingAmount
    };
  };

  const getTotalAmountPaid = () => {
    let totalPaid = 0;
    const monthMap = {};
    
    feeRecords.forEach(record => {
      const month = record.paid_month;
      const paid = parseFloat(record.fee_paid || 0);
      if (!monthMap[month] || paid > monthMap[month]) {
        monthMap[month] = paid;
      }
    });
    
    Object.values(monthMap).forEach(paid => {
      totalPaid += paid;
    });
    
    return totalPaid;
  };

  const getCurrentMonthPaymentDetails = () => {
    const currentMonth = moment().format("MMMM");
    const record = feeRecords
      .filter(r => r.paid_month === currentMonth)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    
    const monthlyFee = parseFloat(studentData.studentFee || 0);
    const paid = record ? parseFloat(record.fee_paid || 0) : 0;
    const discount = record ? parseFloat(record.discount_amount || 0) : 0;
    const payable = monthlyFee - discount;
    const pending = payable - paid;
    
    return {
      total: monthlyFee,
      paid,
      discount,
      payable,
      pending
    };
  };

  // ============ END RECEIPT FUNCTIONS ============

  const checkDatabaseConnection = async () => {
    setIsLoadingDb(true);
    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?test=true",
        { timeout: 5000 }
      );

      if (response.data.test_status === "OK") {
        setIsDbConnected(true);
        return true;
      } else {
        setIsDbConnected(false);
        return false;
      }
    } catch (error) {
      console.error("❌ Database connection test failed:", error);
      setIsDbConnected(false);
      return false;
    } finally {
      setIsLoadingDb(false);
    }
  };

  const handleFixZeroFeeSetup = async () => {
    if (!feeSetupValidationData) return;
    
    setIsAutoFixing(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=fix-zero-fee-setup",
        {
          student_id: feeSetupValidationData.studentId
        }
      );
      
      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          text: response.data.message,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            popup: "swal-wide",
          },
        });
        handleSearch();
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.error || "Failed to fix fee setup",
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            popup: "swal-wide",
          },
        });
      }
    } catch (error) {
      console.error("❌ Fix fee setup error:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred while fixing the fee setup.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "swal-wide",
        },
      });
    } finally {
      setIsAutoFixing(false);
    }
  };

  const fetchPaymentHistoryData = async () => {
    if (!studentData.studentId) return;

    setIsLoadingHistory(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-payment-history",
        {
          student_id: studentData.studentId,
          filter: "all",
          page: 1,
          limit: 20,
        }
      );

      if (response.data.success) {
        setPaymentHistoryData(response.data.data || []);
      }
    } catch (error) {
      console.error("❌ Failed to fetch payment history:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchStatsData = async () => {
    if (!studentData.studentId) return;

    setIsLoadingStats(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-statistics-data",
        {
          student_id: studentData.studentId,
          year: new Date().getFullYear(),
        }
      );

      if (response.data.success) {
        setStatsData(response.data);
      }
    } catch (error) {
      console.error("❌ Failed to fetch statistics:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchQuickStats = async () => {
    if (!studentData.studentId) return;

    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-quick-stats",
        {
          student_id: studentData.studentId,
          year: new Date().getFullYear(),
        }
      );

      if (response.data.success) {
        return response.data.stats;
      }
    } catch (error) {
      console.error("❌ Failed to fetch quick stats:", error);
    }
  };

  const handleRefreshHistory = () => {
    if (activeTab === "history") {
      fetchPaymentHistoryData();
    } else if (activeTab === "stats") {
      fetchStatsData();
    }
  };

  const getCurrentMonthStatus = () => {
    const currentMonth = moment().format("MMMM");
    return getMonthStatus(currentMonth);
  };

  const getCurrentMonthStatusDetails = () => {
    const currentMonth = moment().format("MMMM");
    const status = getMonthStatus(currentMonth);

    const currentMonthRecord = feeRecords
      .filter((r) => r.paid_month === currentMonth)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    if (!currentMonthRecord) {
      return {
        status: "unpaid",
        amount: studentData.studentFee || 0,
        paid: 0,
        pending: studentData.studentFee || 0,
      };
    }

    const feePaid = parseFloat(currentMonthRecord.fee_paid) || 0;
    const monthlyFee = parseFloat(currentMonthRecord.monthly_fee) || 0;
    const feePending = parseFloat(currentMonthRecord.fee_pending) || 0;

    return {
      status: status,
      amount: monthlyFee,
      paid: feePaid,
      pending: feePending,
    };
  };

  const getMonthStatus = (month) => {
    const latestRecord = feeRecords
      .filter((r) => r.paid_month === month)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    if (!latestRecord) return "unpaid";

    const feePaid = parseFloat(latestRecord.fee_paid);
    const monthlyFee = parseFloat(latestRecord.monthly_fee);
    const feePending = parseFloat(latestRecord.fee_pending);

    if (feePending === 0) return "paid";
    if (feePaid > 0 && feePaid < monthlyFee) return "partial";
    return "unpaid";
  };

  const getMonthIcon = (month) => {
    const status = getMonthStatus(month);

    switch (status) {
      case "paid":
        return <FaCheckCircle className="paySoloStdFee-monthIcon" />;
      case "partial":
        return <FaLockOpen className="paySoloStdFee-monthIcon" />;
      case "unpaid":
        return <FaLock className="paySoloStdFee-monthIcon" />;
      default:
        return <FaLock className="paySoloStdFee-monthIcon" />;
    }
  };

  const getCurrentMonthStatusIcon = () => {
    const status = getCurrentMonthStatus();
    switch (status) {
      case "paid":
        return (
          <FaCheckCircle className="paySoloStdFee-currentMonthIcon paid" />
        );
      case "partial":
        return (
          <FaLockOpen className="paySoloStdFee-currentMonthIcon partial" />
        );
      case "unpaid":
        return <FaLock className="paySoloStdFee-currentMonthIcon unpaid" />;
      default:
        return <FaLock className="paySoloStdFee-currentMonthIcon" />;
    }
  };

  const handleCheckboxChange = (month) => {
    setSelectedMonths((prevSelected) =>
      prevSelected.includes(month)
        ? prevSelected.filter((m) => m !== month)
        : [...prevSelected, month]
    );
  };

  const calculateTotalFee = () => {
    let totalFee = 0;
    const monthlyFee = parseFloat(studentData.studentFee) || 0;

    selectedMonths.forEach((month) => {
      const feeRecord = feeRecords?.find(
        (record) => record.paid_month === month
      );

      if (feeRecord) {
        const remainingFee = monthlyFee - parseFloat(feeRecord.fee_paid);
        if (remainingFee > 0) {
          totalFee += remainingFee;
        }
      } else {
        totalFee += monthlyFee;
      }
    });

    return totalFee;
  };

  const calculateDiscountedFee = () => {
    const totalFee = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;

    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (totalFee * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }

    discountAmount = Math.min(discountAmount, totalFee);

    return totalFee - discountAmount;
  };

  const calculateFeeDiscountAmount = () => {
    const totalFee = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;

    if (discountType === "percentage") {
      return (totalFee * discountValue) / 100;
    } else {
      return discountValue;
    }
  };

  const handleFeePaidChange = (e) => {
    const feePaidValue = parseFloat(e.target.value) || 0;
    const discountedTotal = calculateDiscountedFee();

    let pendingAmount = discountedTotal - feePaidValue;

    if (selectedMonths.length > 1) {
      pendingAmount = 0;
    }

    setFeeFormData((prev) => ({
      ...prev,
      feePaid: e.target.value,
      feePending: pendingAmount >= 0 ? pendingAmount.toString() : "0",
    }));
  };

  const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ---------- MAIN SEARCH FUNCTION (UPDATED WITH INACTIVE HANDLING) ----------
  const handleSearch = async () => {
    const fullStudentId = `Std-${studentIdPart2}-${studentIdPart3}`;

    if (!studentIdPart2 || !studentIdPart3) {
      Swal.fire({
        title: "Incomplete ID",
        text: "Please enter complete Student ID",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-wide" },
      });
      return;
    }

    setIsLoading(true);
    // Reset all state flags
    setIsFamilyStudent(false);
    setIsFeeSetupIncomplete(false);
    setIsInactive(false);
    setFamilyStudentData(null);
    setFeeSetupValidationData(null);
    setPaymentHistoryData(null);
    setStatsData(null);
    setInactiveStudentData(null);
    setStudentData({ ...initialStudentState });
    setFeeRecords([]);
    setFeeFormData({ ...initialFeeFormState });
    setSelectedMonths([]);

    try {
      Swal.fire({
        title: "Searching...",
        text: "Please wait while we fetch student data.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await fetch(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=search-student-fee",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ studentId: fullStudentId }),
        }
      );

      const responseData = await response.json();

      // ---------- HANDLE NON-OK RESPONSES ----------
      if (!response.ok) {
        Swal.close();

        // 1️⃣ INACTIVE STUDENT (400 with isInactive)
        if (responseData.isInactive) {
          setIsInactive(true);
          setInactiveStudentData(responseData.student);

          // Still populate student info so user can see who it is, but disabled
          setStudentData({
            studentId: fullStudentId,
            studentName: responseData.student?.name || "",
            fatherName: responseData.student?.father_name || "",
            studentClass: responseData.student?.class || "",
            mobile: responseData.student?.mobile || "",
            b_form_no: responseData.student?.b_form_no || "",
            address: responseData.student?.address || "",
            gender: responseData.student?.gender || "",
            section: responseData.student?.section || "",
            family_status: responseData.student?.family_status || "",
            studentFee: 0,
            pendingFee: 0,
            admissionDate: responseData.student?.created_at || "",
            discount: 0,
          });

          // 🟡 WARNING MODAL (not error) to guide admin
          Swal.fire({
            title: "⚠️ Inactive Student",
            html: `
              <div style="text-align: left;">
                <p><strong>${responseData.student?.name || 'Student'}</strong> is currently <span style="color: #e67e22; font-weight: bold;">INACTIVE</span>.</p>
                <p>Fee payment is not allowed for inactive students.</p>
                <hr>
                <p style="font-size: 0.9rem; color: #7f8c8d;">
                  To process payments, please reactivate the student in the 
                  <strong>Student Management</strong> section first.
                </p>
              </div>
            `,
            icon: "warning",
            confirmButtonText: "Understood",
            customClass: { popup: "swal-wide" },
          });
          setIsLoading(false);
          return;
        }

        // 2️⃣ FEE SETUP INCOMPLETE (400 with isFeeSetupComplete: false)
        if (responseData.isFeeSetupComplete === false) {
          setIsFeeSetupIncomplete(true);
          setFeeSetupValidationData({
            studentId: fullStudentId,
            student: responseData.student || {},
            validationMessage:
              responseData.feeSetupValidation?.message || "Fee setup is incomplete",
            missingFields: responseData.feeSetupValidation?.missingFields || [],
            zeroFields: responseData.feeSetupValidation?.zeroFields || [],
          });

          setStudentData({
            studentId: fullStudentId,
            studentName: responseData.student?.name || "",
            fatherName: responseData.student?.father_name || "",
            studentClass: responseData.student?.class || "",
            mobile: responseData.student?.mobile || "",
            b_form_no: responseData.student?.b_form_no || "",
            address: responseData.student?.address || "",
            gender: responseData.student?.gender || "",
            section: responseData.student?.section || "",
            family_status: responseData.student?.family_status || "",
            studentFee: 0,
            pendingFee: 0,
            admissionDate: responseData.student?.created_at || "",
            discount: 0,
          });

          Swal.fire({
            title: "⚠️ Fee Setup Incomplete",
            html: `
              <div style="text-align: left;">
                <p>${responseData.feeSetupValidation?.message || 'Fee records are missing or incomplete.'}</p>
                <p><strong>Action required:</strong> Please complete the fee setup for this SOLO student before accepting payments.</p>
                ${responseData.feeSetupValidation?.missingFields?.length ? `
                  <hr>
                  <p style="color: #e67e22;">Missing fields: ${responseData.feeSetupValidation.missingFields.join(', ')}</p>
                ` : ''}
              </div>
            `,
            icon: "warning",
            confirmButtonText: "OK",
            customClass: { popup: "swal-wide" },
          });
          setIsLoading(false);
          return;
        }

        // 3️⃣ FAMILY STUDENT (400 with isFamilyStudent)
        if (responseData.isFamilyStudent) {
          setIsFamilyStudent(true);
          setFamilyStudentData(responseData);

          Swal.fire({
            title: "👪 Family Student Detected",
            text: "Family student fees must be managed via the Family Fee Management section.",
            icon: "info",
            confirmButtonText: "OK",
            customClass: { popup: "swal-wide" },
          });
          setIsLoading(false);
          return;
        }

        // 4️⃣ OTHER ERRORS (student not found, server error, etc.)
        const errorMsg = responseData.error || responseData.message || "Student not found";
        Swal.fire({
          title: "Error!",
          text: errorMsg,
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: "swal-wide" },
        });
        setIsLoading(false);
        return;
      }

      // ---------- SUCCESS RESPONSE (200) ----------
      // Reset all warning flags
      setIsInactive(false);
      setIsFamilyStudent(false);
      setIsFeeSetupIncomplete(false);
      setFamilyStudentData(null);
      setFeeSetupValidationData(null);
      setInactiveStudentData(null);

      // Fetch detailed fee records
      const feeResponse = await fetch(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=search-student-fee-details",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ studentId: fullStudentId }),
        }
      );

      if (!feeResponse.ok) {
        throw new Error("Failed to fetch fee details");
      }

      const feeData = await feeResponse.json();
      const feeDetails = Array.isArray(feeData.feeDetails) ? feeData.feeDetails : [];
      setFeeRecords(feeDetails);

      // Calculate pending fee (latest record per month)
      const monthMap = {};
      feeDetails.forEach((record) => {
        const month = record.paid_month;
        if (
          !monthMap[month] ||
          new Date(record.created_at) > new Date(monthMap[month].created_at)
        ) {
          monthMap[month] = record;
        }
      });
      const totalPending = Object.values(monthMap).reduce(
        (sum, record) => sum + parseFloat(record.fee_pending || 0),
        0
      );

      setStudentData({
        studentId: fullStudentId,
        studentName: responseData.student?.name || responseData.student?.student_name || "",
        fatherName: responseData.student?.father_name || responseData.student?.fatherName || "",
        studentClass: responseData.student?.class || "",
        mobile: responseData.student?.mobile || "",
        b_form_no: responseData.student?.b_form_no || "",
        address: responseData.student?.address || "",
        gender: responseData.student?.gender || "",
        section: responseData.student?.section || "",
        family_status: responseData.student?.family_status || "",
        studentFee: feeDetails.length > 0 ? parseFloat(feeDetails[0].monthly_fee) || 0 : 0,
        pendingFee: totalPending,
        admissionDate: responseData.student?.created_at || "",
        discount: responseData.student?.discount || 0,
      });

      setFeeFormData({
        feePaid: "",
        feePending: "0",
        totalFee: calculateTotalFee().toString(),
        discountType: "percentage",
        discountValue: "0",
        paymentMode: "cash",
        transactionId: "",
        remarks: "",
      });
      setSelectedMonths([]);

      checkDatabaseConnection();
      fetchPaymentHistoryData();
      fetchStatsData();

      Swal.fire({
        title: "Success!",
        text: `Student ${responseData.student?.name || "data"} loaded successfully.`,
        icon: "success",
        confirmButtonText: "OK",
        customClass: { popup: "swal-wide" },
      });

    } catch (error) {
      console.error("❌ Fetch Error:", error);
      Swal.close();
      Swal.fire({
        title: "Error!",
        text: error.message || "An error occurred while searching for the student.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: { popup: "swal-wide" },
      });

      // Reset everything
      setStudentData({ ...initialStudentState });
      setFeeRecords([]);
      setFeeFormData({ ...initialFeeFormState });
      setSelectedMonths([]);
      setIsFamilyStudent(false);
      setFamilyStudentData(null);
      setIsFeeSetupIncomplete(false);
      setFeeSetupValidationData(null);
      setIsInactive(false);
      setInactiveStudentData(null);
      setPaymentHistoryData(null);
      setStatsData(null);
    } finally {
      setIsLoading(false);
      Swal.close();
    }
  };

  const handleAutoFixFeeSetup = async () => {
    if (!feeSetupValidationData) return;
    
    setIsAutoFixing(true);
    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=fix-missing-fee-setup",
        {
          student_id: feeSetupValidationData.studentId,
          auto_create: true
        }
      );
      
      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          text: response.data.message || "Fee setup auto-created successfully.",
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            popup: "swal-wide",
          },
        });
        handleSearch();
      } else {
        Swal.fire({
          title: "Auto-fix Failed",
          text: response.data.message || "Failed to auto-create fee setup.",
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            popup: "swal-wide",
          },
        });
      }
    } catch (error) {
      console.error("❌ Auto-fix error:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred while trying to auto-fix the fee setup.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "swal-wide",
        },
      });
    } finally {
      setIsAutoFixing(false);
    }
  };

  const showStudentDetailsModal = () => {
    if (!studentData.studentId) {
      Swal.fire({
        title: "No Data",
        text: "No student data available!",
        icon: "error",
        customClass: {
          popup: "swal-wide",
        },
      });
      return;
    }

    Swal.fire({
      title: "Student Details",
      html: `
        <div class="paySoloStdFee-studentModal">
          <div class="paySoloStdFee-studentModalHeader">
            <FaUser style="margin-right: 10px;" />
            Complete Student Information
          </div>
          <div class="paySoloStdFee-studentModalGrid">
            <div class="paySoloStdFee-studentModalField">
              <label>Student ID</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.studentId
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Student Name</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.studentName
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Father Name</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.fatherName
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Class & Section</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.studentClass
              } - ${studentData.section}</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Mobile</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.mobile
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>B-Form No</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.b_form_no
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Address</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.address
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Gender</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.gender
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Family Status</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.family_status
              }</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Admission Date</label>
              <div class="paySoloStdFee-studentModalValue">${moment(
                studentData.admissionDate
              ).format("DD-MM-YYYY")}</div>
            </div>
            <div class="paySoloStdFee-studentModalField">
              <label>Discount</label>
              <div class="paySoloStdFee-studentModalValue">${
                studentData.discount
              }%</div>
            </div>
          </div>
          <div class="paySoloStdFee-studentModalFooter">
            <div class="paySoloStdFee-studentModalStat">
              <div>Monthly Fee</div>
              <div class="paySoloStdFee-studentModalAmount">${formatCurrency(
                studentData.studentFee
              )}</div>
            </div>
            <div class="paySoloStdFee-studentModalStat">
              <div>Pending Fee</div>
              <div class="paySoloStdFee-studentModalAmount paySoloStdFee-studentModalPending">${formatCurrency(
                calculateTotalPendingFee()
              )}</div>
            </div>
          </div>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Close",
      width: "800px",
      customClass: {
        popup: "swal-wide",
      },
    });
  };

  const handleFeeSubmit = async (event) => {
    event.preventDefault();

    if (selectedMonths.length === 0) {
      Swal.fire({
        title: "No Month Selected",
        text: "Please select at least one month",
        icon: "warning",
        customClass: {
          popup: "swal-wide",
        },
      });
      return;
    }

    const feePaidValue = parseFloat(feeFormData.feePaid) || 0;
    const calculatedTotal = calculateTotalFee();
    const discountType = feeFormData.discountType;
    const discountValue = parseFloat(feeFormData.discountValue) || 0;

    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (calculatedTotal * discountValue) / 100;
    } else {
      discountAmount = discountValue;
    }

    discountAmount = Math.min(discountAmount, calculatedTotal);

    const discountedTotal = calculatedTotal - discountAmount;

    if (selectedMonths.length > 1) {
      if (Math.abs(feePaidValue - discountedTotal) > 0.01) {
        Swal.fire({
          title: "Payment Amount Error",
          html: `
            <div class="validation-error">
              <FaExclamationTriangle style="color: #e74c3c; font-size: 2rem; margin-bottom: 1rem;" />
              <h3>Multiple Months Selected</h3>
              <p>When selecting <strong>${
                selectedMonths.length
              } months</strong>, the payment amount must exactly equal the total fee.</p>
              <div class="validation-details">
                <div><strong>Total Fee:</strong> ${formatCurrency(
                  calculatedTotal
                )}</div>
                <div><strong>Discount (${
                  discountType === "percentage"
                    ? discountValue + "%"
                    : formatCurrency(discountValue)
                }):</strong> ${formatCurrency(discountAmount)}</div>
                <div><strong>Amount Payable:</strong> ${formatCurrency(
                  discountedTotal
                )}</div>
                <div><strong>Amount Paid:</strong> ${formatCurrency(
                  feePaidValue
                )}</div>
              </div>
              <p class="validation-note">For multiple months, you must pay the exact amount (${formatCurrency(
                discountedTotal
              )})</p>
            </div>
          `,
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            popup: "swal-wide",
          },
        });
        return;
      }
    } else if (selectedMonths.length === 1) {
      if (feePaidValue > discountedTotal) {
        Swal.fire({
          title: "Amount Exceeded",
          text: `Fee paid cannot exceed payable amount of ${formatCurrency(
            discountedTotal
          )}`,
          icon: "error",
          customClass: {
            popup: "swal-wide",
          },
        });
        return;
      }
    }

    if (feePaidValue <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid fee amount",
        icon: "warning",
        customClass: {
          popup: "swal-wide",
        },
      });
      return;
    }

    let bulkData = [];

    selectedMonths.forEach((month) => {
      const feeRecord = feeRecords.find(
        (record) => record.paid_month === month
      );
      const monthlyFee = parseFloat(studentData.studentFee) || 0;

      let existingPaid = feeRecord ? parseFloat(feeRecord.fee_paid) : 0;
      let existingPending = feeRecord
        ? parseFloat(feeRecord.fee_pending)
        : monthlyFee;

      let monthShare = feePaidValue * (monthlyFee / calculatedTotal);
      let newPaid = existingPaid + monthShare;
      let newPending = monthlyFee - newPaid;

      bulkData.push({
        student_id: studentData.studentId,
        student_name: studentData.studentName,
        family_name: studentData.fatherName || "",
        family_code: studentData.family_status || "",
        monthly_fee: monthlyFee,
        fee_paid: newPaid,
        fee_pending: newPending > 0 ? newPending : 0,
        discount_type: discountType,
        discount_value: discountValue,
        discount_amount: discountAmount,
        paid_month: month,
        payment_mode: feeFormData.paymentMode,
        transaction_id: feeFormData.transactionId || null,
        remarks: feeFormData.remarks || "",
      });
    });

    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=pay-solo-monthly-fee",
        bulkData
      );

      if (response.data.success) {
        Swal.fire({
          title: "Success!",
          html: `
            <div class="paySoloStdFee-successModal">
              <FaCheckCircle style="color: #27ae60; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>Fee Payment Successful!</h3>
              <p>Fee records submitted successfully!</p>
              <div class="paySoloStdFee-receiptInfo">
                <div><strong>Total Amount:</strong> ${formatCurrency(
                  calculatedTotal
                )}</div>
                <div><strong>Discount Applied:</strong> ${
                  discountType === "percentage"
                    ? discountValue + "%"
                    : formatCurrency(discountValue)
                } (${formatCurrency(discountAmount)})</div>
                <div><strong>Amount Payable:</strong> ${formatCurrency(
                  discountedTotal
                )}</div>
                <div><strong>Amount Paid:</strong> ${formatCurrency(
                  feePaidValue
                )}</div>
                <div><strong>Payment Mode:</strong> ${
                  feeFormData.paymentMode
                }</div>
                <div><strong>Months:</strong> ${selectedMonths.join(", ")}</div>
              </div>
              <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 5px;">
                <h4>Generate Receipt</h4>
                <p>Would you like to generate a receipt for this payment?</p>
                <div style="display: flex; gap: 10px; margin-top: 15px;">
                  <button 
                    onclick="this.closest('.swal2-container').querySelector('.swal2-confirm').click()"
                    style="flex: 1; padding: 10px; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer;"
                  >
                    <FaPrint style="margin-right: 5px;" /> Print Now
                  </button>
                  <button 
                    onclick="this.closest('.swal2-container').querySelector('.swal2-cancel').click()"
                    style="flex: 1; padding: 10px; background: #2ecc71; color: white; border: none; border-radius: 4px; cursor: pointer;"
                  >
                    <FaEye style="margin-right: 5px;" /> View First
                  </button>
                </div>
              </div>
            </div>
          `,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Print Receipt",
          cancelButtonText: "View Receipt",
          customClass: {
            popup: "swal-wide",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            handlePrintReceipt("current");
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            handleViewReceipt();
          }
        });

        handleSearch();

        setSelectedMonths([]);
        setFeeFormData({
          feePaid: "",
          feePending: "0",
          totalFee: "0",
          discountType: "percentage",
          discountValue: "0",
          paymentMode: "cash",
          transactionId: "",
          remarks: "",
        });
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.error || "Failed to submit fee data.",
          icon: "error",
          customClass: {
            popup: "swal-wide",
          },
        });
      }
    } catch (error) {
      console.error("❌ Fee Submission Error:", error);
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "An error occurred during fee submission.",
        icon: "error",
        customClass: {
          popup: "swal-wide",
        },
      });
    }
  };

  const handleClearAll = () => {
    setSelectedMonths([]);
    setFeeFormData({
      feePaid: "",
      feePending: "0",
      totalFee: "0",
      discountType: "percentage",
      discountValue: "0",
      paymentMode: "cash",
      transactionId: "",
      remarks: "",
    });
  };

  const getMonthAmount = (month) => {
    const latestRecord = feeRecords
      .filter((r) => r.paid_month === month)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    if (!latestRecord) return studentData.studentFee || 0;

    const feePending = parseFloat(latestRecord.fee_pending);
    return feePending > 0 ? feePending : 0;
  };

  const handleNavigateToFamilyFee = () => {
    navigate("/family-fee-management");
  };

  const handleNavigateToFeeSetup = () => {
    if (feeSetupValidationData) {
      navigate("/Fee-ApplyTo-Solo-Student", {
        state: {
          studentId: feeSetupValidationData.studentId,
          studentName: feeSetupValidationData.student.name || "",
          className: feeSetupValidationData.student.class || "",
        },
      });
    }
  };

  const handleClearFeeSetupValidation = () => {
    setIsFeeSetupIncomplete(false);
    setFeeSetupValidationData(null);
    setStudentIdPart2("");
    setStudentIdPart3("");
  };

  const currentMonthStatusDetails = getCurrentMonthStatusDetails();

  const getUnpaidMonthsCount = () => {
    return months.filter((m) => getMonthStatus(m) !== "paid").length;
  };

  const getPaidMonthsCount = () => {
    return months.filter((m) => getMonthStatus(m) === "paid").length;
  };

  const getPartialMonthsCount = () => {
    return months.filter((m) => getMonthStatus(m) === "partial").length;
  };

  // ---------- RENDER ----------
  return (
    <div className="paySoloStdFee-main">
      <Sidebar />

      <div className="main-content">
        <header className="paySoloStdFee-header">
          <div className="paySoloStdFee-headerContent">
            <h1 className="paySoloStdFee-title" style={{ color: "white" }}>
              <FaMoneyBillWave className="paySoloStdFee-titleIcon" />
              Student Fee & Expense Management
            </h1>
            <p className="paySoloStdFee-subtitle">
              Pay monthly fees and record expenses for individual students
            </p>
          </div>
        </header>

        {/* Tabs */}
        <div className="paySoloStdFee-tabs">
          <button
            className={`paySoloStdFee-tab ${
              activeTab === "fee" ? "paySoloStdFee-tabActive" : ""
            }`}
            onClick={() => setActiveTab("fee")}
          >
            <FaCreditCard className="paySoloStdFee-tabIcon" />
            Fee Payment
          </button>
          <button
            className={`paySoloStdFee-tab ${
              activeTab === "expense" ? "paySoloStdFee-tabActive" : ""
            }`}
            onClick={() => setActiveTab("expense")}
          >
            <FaMoneyBillWave className="paySoloStdFee-tabIcon" />
            Expense Record
          </button>
          <button
            className={`paySoloStdFee-tab ${
              activeTab === "history" ? "paySoloStdFee-tabActive" : ""
            }`}
            onClick={() => setActiveTab("history")}
          >
            <FaHistory className="paySoloStdFee-tabIcon" />
            Payment History
          </button>
          <button
            className={`paySoloStdFee-tab ${
              activeTab === "stats" ? "paySoloStdFee-tabActive" : ""
            }`}
            onClick={() => setActiveTab("stats")}
          >
            <FaChartLine className="paySoloStdFee-tabIcon" />
            Statistics
          </button>
        </div>

        {/* Search Section */}
        <section className="paySoloStdFee-searchSection">
          <div className="paySoloStdFee-searchContainer">
            <div className="paySoloStdFee-searchGroup">
              <label className="paySoloStdFee-searchLabel">
                <FaIdCard className="paySoloStdFee-searchIcon" />
                Enter Student ID
              </label>
              <div className="paySoloStdFee-idInput">
                <div className="paySoloStdFee-idPrefix">Std</div>
                <span className="paySoloStdFee-idSeparator">-</span>
                <input
                  type="text"
                  className="paySoloStdFee-idField"
                  value={studentIdPart2}
                  onChange={(e) =>
                    setStudentIdPart2(
                      e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
                    )
                  }
                  placeholder="XXX"
                  maxLength="3"
                  autoFocus
                />
                <span className="paySoloStdFee-idSeparator">-</span>
                <input
                  type="text"
                  className="paySoloStdFee-idField"
                  value={studentIdPart3}
                  onChange={(e) =>
                    setStudentIdPart3(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="XXXXXXX"
                  maxLength="7"
                />
              </div>
              <div className="paySoloStdFee-idHint">
                Format: Std-XXX-XXXXXXX (e.g., Std-ABC-1234567)
              </div>
            </div>

            <div className="paySoloStdFee-searchActions">
              <button
                className="paySoloStdFee-searchBtn"
                onClick={handleSearch}
                disabled={isLoading || !studentIdPart2 || !studentIdPart3}
              >
                {isLoading ? (
                  <>
                    <div className="paySoloStdFee-loading"></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <FaSearch className="paySoloStdFee-searchBtnIcon" />
                    Search Student
                  </>
                )}
              </button>

              <button
                className="paySoloStdFee-forgotBtn"
                onClick={() => navigate("/Forget-Student-ID")}
              >
                <FaSearch className="paySoloStdFee-forgotIcon" />
                Forgot ID?
              </button>
            </div>
          </div>
        </section>

        {/* 🔴 NEW: Inactive Student Banner */}
        {isInactive && (
          <div className="paySoloStdFee-inactive-banner">
            <FaBan className="paySoloStdFee-inactive-icon" />
            <div className="paySoloStdFee-inactive-content">
              <h3>⚠️ Inactive Student</h3>
              <p>
                <strong>{studentData.studentName || 'This student'}</strong> is currently marked as <strong>INACTIVE</strong>.
              </p>
              <p className="paySoloStdFee-inactive-instruction">
                Fee payment is disabled. Please reactivate the student in the <strong>Student Management</strong> section to process payments.
              </p>
            </div>
          </div>
        )}

        {/* Family Student Banner */}
        {isFamilyStudent && familyStudentData && (
          <div className="paySoloStdFee-family-warning-banner">
            <FaExclamationTriangle className="paySoloStdFee-warning-icon" />
            <div className="paySoloStdFee-warning-content">
              <h3>Family Student Detected</h3>
              <p>
                <strong>{familyStudentData.student.name}</strong> belongs to a
                family (Family Status:{" "}
                <strong>{familyStudentData.student.family_status}</strong>).
                Family student fees cannot be paid from this screen.
              </p>
              <p>
                Please use the <strong>Family Fee Management</strong> section.
              </p>
            </div>
            <button
              className="paySoloStdFee-family-nav-btn"
              onClick={handleNavigateToFamilyFee}
            >
              <FaArrowRight style={{ marginRight: "8px" }} />
              Go to Family Fee Management
            </button>
          </div>
        )}

        {/* Fee Setup Incomplete Banner */}
        {isFeeSetupIncomplete && feeSetupValidationData && (
          <div className="paySoloStdFee-feeSetup-warning-banner">
            <FaExclamationTriangle className="paySoloStdFee-warning-icon" />
            <div className="paySoloStdFee-warning-content">
              <h3>Fee Setup Incomplete</h3>
              <p>
                <strong>
                  {feeSetupValidationData.student.name || "Student"}
                </strong>{" "}
                (ID: <strong>{feeSetupValidationData.studentId}</strong>) has
                incomplete fee setup.
              </p>
              <p>
                <strong>Issue:</strong>{" "}
                {feeSetupValidationData.validationMessage}
              </p>
              {feeSetupValidationData.zeroFields && feeSetupValidationData.zeroFields.length > 0 && (
                <div className="paySoloStdFee-missing-fields">
                  <strong>Zero-Value Fields:</strong>
                  <ul>
                    {feeSetupValidationData.zeroFields.map(
                      (field, index) => (
                        <li key={index}>
                          {field.replace(/_/g, " ").toUpperCase()}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
              <p className="paySoloStdFee-instruction">
                Please visit the <strong>Fee Setup Section</strong> to configure
                the student's fee structure before processing payments, or use
                the Auto-fix button below.
              </p>
            </div>
            <div className="paySoloStdFee-feeSetup-buttons">
              <button
                className="paySoloStdFee-feeSetup-nav-btn"
                onClick={handleNavigateToFeeSetup}
              >
                <FaCog style={{ marginRight: "8px" }} />
                Go to Fee Setup
              </button>
              <button
                className="paySoloStdFee-feeSetup-auto-fix-btn"
                onClick={handleFixZeroFeeSetup}
                disabled={isAutoFixing}
              >
                {isAutoFixing ? (
                  <>
                    <div className="paySoloStdFee-loading"></div>
                    Auto-fixing...
                  </>
                ) : (
                  <>
                    <FaSync style={{ marginRight: "8px" }} />
                    Auto-fix Zero Fee Setup
                  </>
                )}
              </button>
              <button
                className="paySoloStdFee-feeSetup-clear-btn"
                onClick={handleClearFeeSetupValidation}
              >
                <FaTimes style={{ marginRight: "8px" }} />
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Receipt Options Dropdown (only if student active and no warnings) */}
        {studentData.studentId && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
          <div className="paySoloStdFee-reportOptionsContainer" ref={dropdownRef}>
            <button
              className="paySoloStdFee-reportOptionsBtn"
              onClick={() => setShowReceiptOptions(!showReceiptOptions)}
            >
              <FaFileExport className="paySoloStdFee-reportOptionsIcon" />
              Generate Reports
              <span className="paySoloStdFee-optionsArrow">
                {showReceiptOptions ? "▲" : "▼"}
              </span>
            </button>
            
            {showReceiptOptions && (
              <div className="paySoloStdFee-reportOptionsDropdown">
                <div className="paySoloStdFee-reportOptionsHeader">
                  <FaReceipt />
                  <span>Generate Receipts & Reports</span>
                </div>
                
                <div className="paySoloStdFee-reportOptionsList">
                  <button
                    className="paySoloStdFee-reportOption"
                    onClick={() => {
                      handleViewReceipt();
                      setShowReceiptOptions(false);
                    }}
                    disabled={selectedMonths.length === 0 || !feeFormData.feePaid}
                  >
                    <FaEye className="paySoloStdFee-reportOptionIcon" />
                    <div>
                      <div className="paySoloStdFee-reportOptionTitle">View Current Receipt</div>
                      <div className="paySoloStdFee-reportOptionDesc">
                        Preview current payment receipt
                      </div>
                    </div>
                  </button>
                  
                  <button
                    className="paySoloStdFee-reportOption"
                    onClick={() => {
                      handlePrintReceipt("current");
                      setShowReceiptOptions(false);
                    }}
                    disabled={selectedMonths.length === 0 || !feeFormData.feePaid}
                  >
                    <FaPrint className="paySoloStdFee-reportOptionIcon" />
                    <div>
                      <div className="paySoloStdFee-reportOptionTitle">Print Current Receipt</div>
                      <div className="paySoloStdFee-reportOptionDesc">
                        Print current payment receipt
                      </div>
                    </div>
                  </button>
                  
                  <div className="paySoloStdFee-reportOptionDivider"></div>
                  
                  <button
                    className="paySoloStdFee-reportOption"
                    onClick={() => {
                      handlePrintReceipt("currentMonth");
                      setShowReceiptOptions(false);
                    }}
                  >
                    <FaCalendarDay className="paySoloStdFee-reportOptionIcon" />
                    <div>
                      <div className="paySoloStdFee-reportOptionTitle">Current Month Report</div>
                      <div className="paySoloStdFee-reportOptionDesc">
                        Generate current month fee report
                      </div>
                    </div>
                  </button>
                  
                  <button
                    className="paySoloStdFee-reportOption"
                    onClick={() => {
                      handlePrintReceipt("complete");
                      setShowReceiptOptions(false);
                    }}
                  >
                    <FaFileAlt className="paySoloStdFee-reportOptionIcon" />
                    <div>
                      <div className="paySoloStdFee-reportOptionTitle">Complete Fee Report</div>
                      <div className="paySoloStdFee-reportOptionDesc">
                        Full annual fee report with breakdown
                      </div>
                    </div>
                  </button>
                  
                  <div className="paySoloStdFee-reportOptionDivider"></div>
                  
                  <button
                    className="paySoloStdFee-reportOption"
                    onClick={() => {
                      handleExportPDF("complete");
                      setShowReceiptOptions(false);
                    }}
                    disabled={isGeneratingReport}
                  >
                    {isGeneratingReport ? (
                      <div className="paySoloStdFee-loading"></div>
                    ) : (
                      <FaFilePdf className="paySoloStdFee-reportOptionIcon" />
                    )}
                    <div>
                      <div className="paySoloStdFee-reportOptionTitle">
                        {isGeneratingReport ? "Generating PDF..." : "Export as PDF"}
                      </div>
                      <div className="paySoloStdFee-reportOptionDesc">
                        Download complete report as PDF
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top Cards (only if student active and no warnings) */}
        {studentData.studentId && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && activeTab === "fee" && (
          <div className="paySoloStdFee-topCards">
            <div className="paySoloStdFee-topCard paySoloStdFee-pendingFeeCard">
              <div className="paySoloStdFee-topCardHeader">
                <FaMoneyBillWave className="paySoloStdFee-topCardIcon" />
                <h3>Total Pending Fee</h3>
              </div>
              <div className="paySoloStdFee-topCardContent">
                <div className="paySoloStdFee-topCardValue">
                  {formatCurrency(calculateTotalPendingFee())}
                </div>
                <div className="paySoloStdFee-topCardSubtitle">
                  Based on latest fee records
                </div>
                <div className="paySoloStdFee-topCardDetails">
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Unpaid Months:</span>
                    <strong>{getUnpaidMonthsCount()}</strong>
                  </div>
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Partial Months:</span>
                    <strong>{getPartialMonthsCount()}</strong>
                  </div>
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Paid Months:</span>
                    <strong>{getPaidMonthsCount()}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`paySoloStdFee-topCard paySoloStdFee-currentMonthCard status-${currentMonthStatusDetails.status}`}
            >
              <div className="paySoloStdFee-topCardHeader">
                <FaCalendarDay className="paySoloStdFee-topCardIcon" />
                <h3>Current Month Status</h3>
                <span className="paySoloStdFee-currentMonthName">
                  {moment().format("MMMM")}
                </span>
              </div>
              <div className="paySoloStdFee-topCardContent">
                <div className="paySoloStdFee-currentMonthStatus">
                  {getCurrentMonthStatusIcon()}
                  <span
                    className={`paySoloStdFee-statusText ${currentMonthStatusDetails.status}`}
                  >
                    {currentMonthStatusDetails.status.toUpperCase()}
                  </span>
                </div>
                <div className="paySoloStdFee-topCardDetails">
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Monthly Fee:</span>
                    <strong>
                      {formatCurrency(currentMonthStatusDetails.amount)}
                    </strong>
                  </div>
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Amount Paid:</span>
                    <strong>
                      {formatCurrency(currentMonthStatusDetails.paid)}
                    </strong>
                  </div>
                  <div className="paySoloStdFee-topCardDetail">
                    <span>Amount Pending:</span>
                    <strong className="pending">
                      {formatCurrency(currentMonthStatusDetails.pending)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Student Information Card (even for inactive, show basic info) */}
        {studentData.studentId && !isFamilyStudent && !isFeeSetupIncomplete && (
          <div className={`paySoloStdFee-studentCard ${isInactive ? 'paySoloStdFee-studentCard--inactive' : ''}`}>
            <div className="paySoloStdFee-studentCardHeader">
              <FaUser className="paySoloStdFee-studentIcon" />
              <h3>Student Information</h3>
              <div className="paySoloStdFee-studentBadge">
                {studentData.studentClass} - {studentData.section}
              </div>
              
              {/* Quick Report Buttons – hidden for inactive */}
              {!isInactive && (
                <div className="paySoloStdFee-quickReportButtons">
                  <button
                    className="paySoloStdFee-quickReportBtn"
                    onClick={() => handlePrintReceipt("currentMonth")}
                    title="Generate Current Month Report"
                  >
                    <FaCalendarDay />
                    <span>Current Month</span>
                  </button>
                  <button
                    className="paySoloStdFee-quickReportBtn"
                    onClick={() => handlePrintReceipt("complete")}
                    title="Generate Complete Fee Report"
                  >
                    <FaFileAlt />
                    <span>Full Report</span>
                  </button>
                  <button
                    className="paySoloStdFee-quickReportBtn"
                    onClick={handleExportPDF}
                    disabled={isGeneratingReport}
                    title="Export as PDF"
                  >
                    {isGeneratingReport ? (
                      <div className="paySoloStdFee-loading"></div>
                    ) : (
                      <FaFilePdf />
                    )}
                    <span>{isGeneratingReport ? "Generating..." : "Export PDF"}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="paySoloStdFee-studentInfo">
              <div className="paySoloStdFee-studentRow">
                <div className="paySoloStdFee-studentField">
                  <div className="paySoloStdFee-studentFieldIcon">
                    <FaUser />
                  </div>
                  <div>
                    <div className="paySoloStdFee-fieldLabel">Student Name</div>
                    <div className="paySoloStdFee-fieldValue">
                      {studentData.studentName}
                    </div>
                  </div>
                </div>

                <div className="paySoloStdFee-studentField">
                  <div className="paySoloStdFee-studentFieldIcon">
                    <FaUser />
                  </div>
                  <div>
                    <div className="paySoloStdFee-fieldLabel">Father Name</div>
                    <div className="paySoloStdFee-fieldValue">
                      {studentData.fatherName}
                    </div>
                  </div>
                </div>

                <div className="paySoloStdFee-studentField">
                  <div className="paySoloStdFee-studentFieldIcon">
                    <FaIdCard />
                  </div>
                  <div>
                    <div className="paySoloStdFee-fieldLabel">Student ID</div>
                    <div className="paySoloStdFee-fieldValue">
                      {studentData.studentId}
                    </div>
                  </div>
                </div>
              </div>

              <div className="paySoloStdFee-studentActions">
                <button
                  className="paySoloStdFee-viewDetailsBtn"
                  onClick={showStudentDetailsModal}
                >
                  <FaEye className="paySoloStdFee-viewIcon" />
                  View Complete Details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div
          className={`paySoloStdFee-content ${
            isFamilyStudent || isFeeSetupIncomplete || isInactive ? "paySoloStdFee-disabled" : ""
          }`}
        >
          {/* Fee Payment Tab – DISABLED if inactive */}
          {studentData.studentId && activeTab === "fee" && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
            <div className="paySoloStdFee-feeContainer">
              <div className="paySoloStdFee-feeFormFull">
                <div className="paySoloStdFee-monthSection">
                  <div className="paySoloStdFee-monthHeader">
                    <FaCalendarAlt className="paySoloStdFee-monthHeaderIcon" />
                    <h3>Select Payment Months</h3>
                    <div className="paySoloStdFee-monthYear">
                      Year: {currentYear}
                    </div>
                  </div>

                  <div className="paySoloStdFee-monthGrid">
                    {months.map((month, index) => {
                      const status = getMonthStatus(month);
                      const isSelected = selectedMonths.includes(month);
                      const amount = getMonthAmount(month);

                      return (
                        <div
                          key={index}
                          className={`paySoloStdFee-monthCard ${status} ${
                            isSelected
                              ? "paySoloStdFee-monthCardSelected"
                              : ""
                          }`}
                          onClick={() => handleCheckboxChange(month)}
                          title={`${month} - ${formatCurrency(amount)}`}
                        >
                          <div className="paySoloStdFee-monthCardHeader">
                            {getMonthIcon(month)}
                            <span className="paySoloStdFee-monthName">
                              {month.substring(0, 3)}
                            </span>
                          </div>

                          <div className="paySoloStdFee-monthAmount">
                            <FaRupeeSign className="paySoloStdFee-rupeeIcon" />
                            {amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>

                          <div className="paySoloStdFee-monthStatus">
                            <span
                              className={`paySoloStdFee-statusIndicator ${status}`}
                            >
                              {status.charAt(0).toUpperCase() +
                                status.slice(1)}
                            </span>
                          </div>

                          {isSelected && (
                            <div className="paySoloStdFee-monthCheck">
                              <FaCheckCircle />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {selectedMonths.length > 0 && (
                    <div className="paySoloStdFee-monthSummary">
                      <div className="paySoloStdFee-summaryItem">
                        <span>Selected Months</span>
                        <div className="paySoloStdFee-summaryValue">
                          <strong>{selectedMonths.length}</strong>
                          <span>Months</span>
                        </div>
                      </div>

                      <div className="paySoloStdFee-summaryItem">
                        <span>Total Amount</span>
                        <div className="paySoloStdFee-summaryValue">
                          <strong className="paySoloStdFee-totalAmount">
                            {formatCurrency(calculateTotalFee())}
                          </strong>
                        </div>
                      </div>

                      <div className="paySoloStdFee-summaryItem">
                        <span>Status</span>
                        <div className="paySoloStdFee-summaryValue">
                          <strong
                            className={`paySoloStdFee-statusBadge ${
                              selectedMonths.length === 1
                                ? "single"
                                : "multiple"
                            }`}
                          >
                            {selectedMonths.length === 1
                              ? "Single Month"
                              : "Multiple Months"}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="paySoloStdFee-paymentForm">
                  <div className="paySoloStdFee-formHeader">
                    <FaCreditCard className="paySoloStdFee-formHeaderIcon" />
                    <h3>Fee Payment Details</h3>
                  </div>

                  <form onSubmit={handleFeeSubmit}>
                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Monthly Fee
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput"
                          value={formatCurrency(studentData.studentFee)}
                          readOnly
                        />
                      </div>

                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaCalendarAlt className="paySoloStdFee-labelIcon" />
                          Selected Months
                        </label>
                        <div className="paySoloStdFee-selectedMonths">
                          {selectedMonths.length > 0 ? (
                            selectedMonths.map((month, index) => (
                              <span
                                key={index}
                                className="paySoloStdFee-monthTag"
                                onClick={() => handleCheckboxChange(month)}
                              >
                                {month}
                                <FaTimes className="paySoloStdFee-monthTagClose" />
                              </span>
                            ))
                          ) : (
                            <span className="paySoloStdFee-noMonths">
                              No months selected. Please select months from
                              above.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Total Fee Amount
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput"
                          value={formatCurrency(calculateTotalFee())}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaPercent className="paySoloStdFee-labelIcon" />
                          Discount Type
                        </label>
                        <select
                          className="paySoloStdFee-formSelect"
                          value={feeFormData.discountType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const totalFee = calculateTotalFee();
                            let newDiscountValue =
                              parseFloat(feeFormData.discountValue) || 0;

                            if (
                              newType === "percentage" &&
                              feeFormData.discountType === "fixed"
                            ) {
                              newDiscountValue =
                                totalFee > 0
                                  ? (newDiscountValue / totalFee) * 100
                                  : 0;
                            } else if (
                              newType === "fixed" &&
                              feeFormData.discountType === "percentage"
                            ) {
                              newDiscountValue =
                                (totalFee * newDiscountValue) / 100;
                            }

                            setFeeFormData((prev) => ({
                              ...prev,
                              discountType: newType,
                              discountValue: newDiscountValue.toString(),
                            }));
                          }}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (Rs.)</option>
                        </select>
                      </div>

                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Discount{" "}
                          {feeFormData.discountType === "percentage"
                            ? "(%)"
                            : "(Rs.)"}
                          <span className="paySoloStdFee-optional-label">
                            (Optional)
                          </span>
                        </label>
                        <input
                          type="number"
                          className="paySoloStdFee-formInput"
                          value={feeFormData.discountValue}
                          onChange={(e) => {
                            setFeeFormData((prev) => ({
                              ...prev,
                              discountValue: e.target.value,
                            }));
                          }}
                          placeholder={
                            feeFormData.discountType === "percentage"
                              ? "0"
                              : "0"
                          }
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Discount Amount
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput paySoloStdFee-discountedAmount"
                          value={formatCurrency(calculateFeeDiscountAmount())}
                          readOnly
                        />
                      </div>

                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Amount After Discount
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput paySoloStdFee-discountedAmount"
                          value={formatCurrency(calculateDiscountedFee())}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Fee Paid
                        </label>
                        <input
                          type="number"
                          className="paySoloStdFee-formInput"
                          value={feeFormData.feePaid}
                          onChange={handleFeePaidChange}
                          placeholder="Enter amount"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Fee Pending
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput"
                          value={formatCurrency(feeFormData.feePending)}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="paySoloStdFee-formRow">
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaMoneyBillWave className="paySoloStdFee-labelIcon" />
                          Payment Mode
                        </label>
                        <select
                          className="paySoloStdFee-formSelect"
                          value={feeFormData.paymentMode}
                          onChange={(e) =>
                            setFeeFormData((prev) => ({
                              ...prev,
                              paymentMode: e.target.value,
                            }))
                          }
                        >
                          <option value="cash">Cash</option>
                          <option value="bank">Bank Transfer</option>
                          <option value="card">Card</option>
                          <option value="online">Online Payment</option>
                        </select>
                      </div>
                    </div>

                    {feeFormData.paymentMode !== "cash" && (
                      <div className="paySoloStdFee-formGroup">
                        <label className="paySoloStdFee-formLabel">
                          <FaIdCard className="paySoloStdFee-labelIcon" />
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          className="paySoloStdFee-formInput"
                          value={feeFormData.transactionId}
                          onChange={(e) =>
                            setFeeFormData((prev) => ({
                              ...prev,
                              transactionId: e.target.value,
                            }))
                          }
                          placeholder="Enter transaction ID"
                        />
                      </div>
                    )}

                    <div className="paySoloStdFee-formGroup">
                      <label className="paySoloStdFee-formLabel">
                        <FaEdit className="paySoloStdFee-labelIcon" />
                        Remarks (Optional)
                      </label>
                      <textarea
                        className="paySoloStdFee-formTextarea"
                        value={feeFormData.remarks}
                        onChange={(e) =>
                          setFeeFormData((prev) => ({
                            ...prev,
                            remarks: e.target.value,
                          }))
                        }
                        placeholder="Enter any remarks..."
                        rows="2"
                      />
                    </div>

                    <div className="paySoloStdFee-formActions">
                      <button
                        type="button"
                        className="paySoloStdFee-clearBtn"
                        onClick={handleClearAll}
                        disabled={
                          selectedMonths.length === 0 && !feeFormData.feePaid
                        }
                      >
                        <FaTimes className="paySoloStdFee-clearIcon" />
                        Clear All
                      </button>

                      <button
                        type="button"
                        className="paySoloStdFee-viewReceiptBtn"
                        onClick={handleViewReceipt}
                        disabled={
                          selectedMonths.length === 0 || !feeFormData.feePaid
                        }
                      >
                        <FaEye className="paySoloStdFee-viewIcon" />
                        View Receipt
                      </button>

                      <button
                        type="button"
                        className="paySoloStdFee-printBtn"
                        onClick={() => handlePrintReceipt("current")}
                        disabled={
                          selectedMonths.length === 0 || !feeFormData.feePaid
                        }
                      >
                        <FaPrint className="paySoloStdFee-printIcon" />
                        Print Receipt
                      </button>

                      <button
                        type="submit"
                        className="paySoloStdFee-submitBtn"
                        disabled={
                          selectedMonths.length === 0 || !feeFormData.feePaid
                        }
                      >
                        <FaPaperPlane className="paySoloStdFee-submitIcon" />
                        Submit Fee Payment
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* 🔴 NEW: Inactive Student Placeholder – shown when inactive and on Fee tab */}
          {studentData.studentId && isInactive && activeTab === "fee" && (
            <div className="paySoloStdFee-inactive-placeholder">
              <FaBan className="paySoloStdFee-inactive-placeholder-icon" />
              <h3>Payments Disabled</h3>
              <p>This student is currently inactive. Fee payment is not allowed.</p>
              <p className="paySoloStdFee-inactive-placeholder-note">
                Please reactivate the student in the <strong>Student Management</strong> section first.
              </p>
            </div>
          )}

          {/* Expense Tab */}
          {studentData.studentId && activeTab === "expense" && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
            <div className="paySoloStdFee-expenseTab">
              <div className="paySoloStdFee-sectionHeader">
                <h2 className="paySoloStdFee-sectionTitle">
                  <FaMoneyBillWave className="paySoloStdFee-sectionIcon" />
                  Expense Management
                </h2>
                <p className="paySoloStdFee-sectionDescription">
                  Manage all expense records for {studentData.studentName}
                </p>
              </div>
              <SoloStudentExpense
                studentData={studentData}
                onRefresh={handleSearch}
                fullScreenMode={true}
              />
            </div>
          )}

          {/* History Tab */}
          {studentData.studentId && activeTab === "history" && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
            <div className="paySoloStdFee-historyTab">
              <PaymentHistoryStatistics
                studentData={studentData}
                isFamilyStudent={false}
                familyData={null}
                initialActiveTab="history"
                enableAutoFetch={true}
                onRefresh={handleRefreshHistory}
                isLoading={isLoadingHistory}
                initialData={paymentHistoryData}
                showStatisticsOnly={false}
              />
            </div>
          )}

          {/* Statistics Tab */}
          {studentData.studentId && activeTab === "stats" && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
            <div className="paySoloStdFee-statsTab">
              <SoloPaymentStatistics
                studentData={studentData}
                onRefresh={handleRefreshHistory}
                isLoading={isLoadingStats}
                initialData={statsData}
              />
            </div>
          )}

          {/* Empty State */}
          {!studentData.studentId && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
            <div className="paySoloStdFee-emptyState">
              <div className="paySoloStdFee-emptyStateIcon">
                <FaSearch />
              </div>
              <h3>Search for a Student</h3>
              <p>
                Enter a Student ID above to view and manage fee payments and
                expenses.
              </p>
              <div className="paySoloStdFee-emptyStateTips">
                <h4>Tips:</h4>
                <ul>
                  <li>Student ID format: Std-XXX-XXXXXXX</li>
                  <li>Example: Std-ABC-1234567</li>
                  <li>Use "Forgot ID?" if you don't know the student ID</li>
                  <li>
                    Family students will be redirected to Family Fee
                    Management
                  </li>
                  <li>Students with incomplete fee setup will be shown</li>
                  <li>Use the "Auto-fix Fee Setup" button to fix zero-value fee fields</li>
                  <li>Generate receipts and reports after successful payments</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer (only if student active and not inactive) */}
        {studentData.studentId && !isFamilyStudent && !isFeeSetupIncomplete && !isInactive && (
          <footer className="paySoloStdFee-footer">
            <div className="paySoloStdFee-footerContent">
              <div className="paySoloStdFee-footerLeft">
                <div className="paySoloStdFee-footerLogo">
                  <FaMoneyBillWave className="paySoloStdFee-footerIcon" />
                  School Fee System
                </div>
                <p className="paySoloStdFee-footerText">
                  Managing fees and expenses for {studentData.studentName} |
                  Class: {studentData.studentClass} | ID:{" "}
                  {studentData.studentId}
                </p>
              </div>

              <div className="paySoloStdFee-footerRight">
                <div className="paySoloStdFee-systemStatus">
                  <div className="paySoloStdFee-statusDot paySoloStdFee-statusActive"></div>
                  System Active
                </div>
                <div className="paySoloStdFee-lastUpdated">
                  Last updated: {moment().format("DD MMM YYYY, hh:mm A")}
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default PaySoloStdFee;